import { Input, InputProps } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

export enum DateFormats {
  'DD/MM/YYYY',
  'YYYY-MM-DD',
}

type DateElements = {
  day: string;
  month: string;
  year: string;
  s1: string;
  s2: string;
  rest: string;
};

const reDisallowedChars: Record<DateFormats, RegExp> = {
  [DateFormats['DD/MM/YYYY']]: /[^0-9\/]/g,
  [DateFormats['YYYY-MM-DD']]: /[^0-9-]/g,
};

// match DD/MM/YYYY + string rest
const reDDMMYYYY = /^(\d{2})?(\/)?(\d{2})?(\/)?(\d{4})?(.*)$/;

export const getDDMMYYYY = (value: string): DateElements => {
  const separator = '/';
  const matches = reDDMMYYYY.exec(value);
  const day = matches?.[1] || '';
  const month = matches?.[3] || '';
  const year = matches?.[5] || '';
  const rest = matches?.[6] || '';
  const s1 = day && (rest || month || matches?.[2]) ? separator : '';
  const s2 = month && (rest || year || matches?.[4]) ? separator : '';
  return { day, month, year, s1, s2, rest };
};

export const applyMaskDDMMYYYY = (value: string) => {
  const { day, month, year, s1, s2, rest } = getDDMMYYYY(value);
  const cleanRest = rest.replace(/^\/+/, ''); // Remove leading slashes from rest
  return `${day}${s1}${month}${s2}${year}${cleanRest}`;
};

export const getFormat = (value: string): DateFormats | null => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return DateFormats['DD/MM/YYYY'];
  return null;
};

export const isValidDate = (maybeDate: Date): maybeDate is Date =>
  Object.prototype.toString.call(maybeDate) === '[object Date]' &&
  !Number.isNaN(maybeDate.getTime());

export const dateStrToUtcDate = (dateStr: string): Date | null => {
  const format = getFormat(dateStr.trim());
  if (format !== DateFormats['DD/MM/YYYY']) return null;

  const { day, month, year } = getDDMMYYYY(dateStr);

  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0));

  return isValidDate(date) ? date : null;
};

/**
 * Convert a date to YYYY-MM-DD.
 */
export const toIsoDate = (value: string) => {
  const date = dateStrToUtcDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
};

/**
 * Convert a date to DD/MM/YYYY.
 */
export const toDateDDMMYYYY = (value: string) => {
  const date = dateStrToUtcDate(value);
  return date ? date.toLocaleDateString('fr-FR') : '';
};

export function onDateInputType(typedValue: string) {
  const rawValue = typedValue.replace(reDisallowedChars[DateFormats['DD/MM/YYYY']], '');
  return applyMaskDDMMYYYY(rawValue);
}

/**
 * Input with a mask for dates, either YYYY-MM-DD or DD/MM/YYYY.
 */
export const DateInput: React.FC<InputProps> = ({
  defaultValue,
  value: valueProp,
  onChange,
  ...otherProps
}) => {
  const initial = valueProp ?? defaultValue;
  const [componentValue, setComponentValue] = useState(toDateDDMMYYYY(String(initial ?? '')));

  const prevValueProp = useRef(valueProp);
  useEffect(() => {
    if (prevValueProp.current !== undefined && !valueProp) setComponentValue('');
    prevValueProp.current = valueProp;
  }, [valueProp]);

  return (
    <Input
      {...otherProps}
      maxLength={10}
      value={componentValue}
      onChange={(e) => {
        const value = onDateInputType(e.target.value);
        setComponentValue(value);
        if (onChange) onChange(e);
      }}
    />
  );
};
